import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Loader2, FileText } from "lucide-react";

export default function TemplateWizardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const caseId = params.get("case");
  const [tpl, setTpl] = useState(null);
  const [answers, setAnswers] = useState({});
  const [preview, setPreview] = useState("");
  const [loadingAi, setLoadingAi] = useState(false);
  const [title, setTitle] = useState("");

  useEffect(() => {
    api.get(`/templates/${id}`).then((r) => {
      setTpl(r.data);
      setTitle(r.data.title);
      setPreview(r.data.body);
    });
  }, [id]);

  if (!tpl) return <div className="label-eyebrow">Loading template…</div>;

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const fillTokens = () => {
    let body = tpl.body;
    for (const q of tpl.questions) {
      const token = `[${q.key.toUpperCase()}]`;
      if (answers[q.key]) body = body.split(token).join(answers[q.key]);
    }
    setPreview(body);
    toast.success("Preview updated (local fill)");
  };

  const aiGenerate = async () => {
    setLoadingAi(true);
    try {
      const { data } = await api.post("/ai/draft-from-template", {
        template_id: id,
        answers,
      });
      setPreview(data.content);
      toast.success("Draft generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI generation failed");
    } finally {
      setLoadingAi(false);
    }
  };

  const saveDraft = async () => {
    const { data } = await api.post("/drafts", {
      case_id: caseId || null,
      template_id: id,
      title,
      content: preview,
      answers,
    });
    toast.success("Draft saved");
    navigate(`/app/drafts/${data.id}`);
  };

  return (
    <div data-testid={`template-wizard-${id}`}>
      <button onClick={() => navigate("/app/templates")} className="text-sm text-[#4A5568] hover:text-[#2A0F5A] inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={14} /> All templates
      </button>
      <PageHeader
        eyebrow={tpl.category}
        title={tpl.title}
        description={tpl.description}
      />

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-5 border border-[#E8E3F0] bg-white p-6" data-testid="wizard-form">
          <div className="label-eyebrow">Answer the questions</div>
          {tpl.questions.map((q) => (
            <div key={q.key}>
              <Label className="label-eyebrow mb-2 block">{q.label}</Label>
              {q.type === "textarea" ? (
                <Textarea
                  rows={3}
                  value={answers[q.key] || ""}
                  onChange={(e) => set(q.key, e.target.value)}
                  className="rounded-none border-[#E8E3F0] bg-white focus-visible:ring-0 focus-visible:border-[#2A0F5A]"
                  data-testid={`wizard-${q.key}`}
                />
              ) : (
                <Input
                  value={answers[q.key] || ""}
                  onChange={(e) => set(q.key, e.target.value)}
                  className="rounded-none border-[#E8E3F0] bg-white focus-visible:ring-0 focus-visible:border-[#2A0F5A]"
                  data-testid={`wizard-${q.key}`}
                />
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-4">
            <Button variant="outline" className="rounded-none border-[#E8E3F0]" onClick={fillTokens} data-testid="wizard-fill-btn">
              <FileText size={12} className="mr-1" /> Fill tokens
            </Button>
            <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" onClick={aiGenerate} disabled={loadingAi} data-testid="wizard-ai-btn">
              {loadingAi ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              <span className="ml-1">Draft with AI</span>
            </Button>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-4">
          <div>
            <Label className="label-eyebrow mb-2 block">Draft title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="rounded-none border-[#E8E3F0] bg-white focus-visible:ring-0 focus-visible:border-[#2A0F5A]"
              data-testid="wizard-title-input"
            />
          </div>
          <div className="border border-[#E8E3F0] bg-white p-6">
            <div className="label-eyebrow mb-3">Preview</div>
            <Textarea
              rows={22}
              value={preview}
              onChange={(e) => setPreview(e.target.value)}
              className="font-mono text-[13px] rounded-none border-[#E8E3F0] bg-[#FAFAF8] focus-visible:ring-0 focus-visible:border-[#2A0F5A] whitespace-pre-wrap"
              data-testid="wizard-preview-textarea"
            />
          </div>
          <div className="flex gap-2">
            <Button className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" onClick={saveDraft} data-testid="wizard-save-draft-btn">Save as draft</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
